
import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from "@/components/ui/card";
import { Toggle } from "@/components/ui/toggle";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Info } from "lucide-react";

interface SigmaCaparolData {
  sigma: Array<{
    reason: string;
    percentage: number;
    value: number;
  }>;
  caparol: Array<{
    reason: string;
    percentage: number;
    value: number;
  }>;
}

interface BrandComparisonProps {
  sigmaVsCaparol: SigmaCaparolData;
}

const BrandComparison: React.FC<BrandComparisonProps> = ({ sigmaVsCaparol }) => {
  const [showPercentage, setShowPercentage] = useState<boolean>(true);
  
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold">6. 🔍 Vergelijking Sigma vs Caparol</h2>
      <Card className="overflow-hidden hover:shadow-md transition-shadow duration-300">
        <CardHeader className="flex flex-row items-center justify-between bg-muted/30">
          <div>
            <CardTitle className="text-base">Vergelijking van schadeoorzaken</CardTitle>
            <CardDescription>Sigma vs Caparol</CardDescription>
          </div>
          <Toggle 
            pressed={showPercentage} 
            onPressedChange={setShowPercentage}
            className="ml-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            aria-label="Toon percentages"
          >
            {showPercentage ? "%" : "#"}
          </Toggle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={sigmaVsCaparol.sigma.map((item, i) => ({
                  reason: item.reason,
                  Sigma: showPercentage ? item.percentage : item.value,
                  Caparol: showPercentage ? sigmaVsCaparol.caparol[i].percentage : sigmaVsCaparol.caparol[i].value
                }))}
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                barGap={8}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  type="number"
                  tick={{ fill: '#4B5563', fontSize: 12 }}
                  tickLine={{ stroke: '#9CA3AF' }}
                  axisLine={{ stroke: '#9CA3AF' }}
                />
                <YAxis 
                  dataKey="reason" 
                  type="category"
                  width={100}
                  tick={{ fill: '#4B5563', fontSize: 12 }}
                  tickLine={{ stroke: '#9CA3AF' }}
                  axisLine={{ stroke: '#9CA3AF' }}
                />
                <Tooltip 
                  formatter={(value) => [
                    `${value}${showPercentage ? '%' : ''}`,
                    ""
                  ]}
                  wrapperStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #ccc', 
                    borderRadius: '6px',
                    boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1)' 
                  }}
                  contentStyle={{ 
                    padding: '10px', 
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '10px' }}
                  iconType="circle"
                  iconSize={10}
                  formatter={(value) => <span className="text-sm font-medium">{value}</span>}
                />
                <Bar 
                  dataKey="Sigma" 
                  fill="#0072BC" 
                  radius={[0, 4, 4, 0]}
                  animationDuration={800}
                  animationEasing="ease-out"
                  className="hover:opacity-80 transition-opacity duration-300"
                  style={{ filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1))' }}
                />
                <Bar 
                  dataKey="Caparol" 
                  fill="#FF3B30" 
                  radius={[0, 4, 4, 0]}
                  animationDuration={1000}
                  animationEasing="ease-out"
                  className="hover:opacity-80 transition-opacity duration-300"
                  style={{ filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1))' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-6 pt-4 border-t">
            <div className="flex items-start gap-2">
              <Info size={18} className="text-muted-foreground mt-0.5" />
              <div>
                <h3 className="font-medium mb-2">Feiten:</h3>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Caparol: helft van de schade door fysieke beschadiging</li>
                  <li>Sigma: meer verspreide verdeling over oorzaken</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default BrandComparison;
